import { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, Mail, Sparkles, CheckCircle2, ArrowLeft, ArrowRight, Rocket } from "lucide-react";

/** /platform/organisaties/nieuw — 3-staps kickstart wizard voor platform-super.
 *  Stap 1: Intake (naam + slug + KVK + directeur-email)
 *  Stap 2: Opties (welkomstmail + demo-data + starter-templates)
 *  Stap 3: Bevestiging + resultaat + eerste-week checklist link
 */
export default function PlatformNewOrganizationPage() {
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ name: "", slug: "", kvk: "", email: "", seed_demo: false });
  const [result, setResult] = useState(null);
  const navigate = useNavigate();

  const setF = (k) => (v) => setForm(f => ({ ...f, [k]: (typeof v === "object" && v?.target) ? v.target.value : v }));

  // Server _slugify: bewaart accenten en normaliseert alleen spaces/case.
  // We mirror die logica hier zodat de preview de daadwerkelijke slug toont.
  const slugPreview = (form.slug || form.name || "").toLowerCase()
    .replace(/[^a-z0-9à-ÿ\s-]/g, "").trim().replace(/\s+/g, "-").slice(0, 40);

  const validateStep1 = () => {
    if (!form.name.trim()) return toast.error("Naam is verplicht"), false;
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Geldig directeur-e-mailadres is verplicht"), false;
    return true;
  };

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/organizations", {
        name: form.name.trim(),
        slug: form.slug.trim() || undefined,
        kvk: form.kvk.trim() || undefined,
        email: form.email.trim(),
      });
      const orgId = r.data?.organization?.id || r.data?.id;
      // Backend response contract: { organization, onboarding_session, starter_seed, welcome_email }
      const email_result = r.data?.welcome_email || r.data?.magic_link_result || null;
      const seed_backend = r.data?.starter_seed || r.data?.seed_result || null;
      // Optioneel: extra starter-templates als user dat wilde
      let demoResult = null;
      if (form.seed_demo && orgId) {
        try {
          await api.post(`/organizations/${orgId}/seed-templates`);
          demoResult = { ok: true };
        } catch (e) { demoResult = { ok: false, error: e?.response?.data?.detail || e.message }; }
      }
      setResult({ ...r.data, org_id: orgId, email_result, seed_backend, demo_seed: demoResult });
      setStep(3);
      toast.success(`Organisatie "${form.name}" aangemaakt`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="platform-new-org-page">
      <Button variant="ghost" onClick={() => navigate("/platform/organisaties")} className="text-neutral-600 hover:text-neutral-900 hover:bg-neutral-100 mb-4" data-testid="wizard-back-btn">
        <ArrowLeft className="h-4 w-4 mr-2" /> Terug naar overzicht
      </Button>

      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-neutral-900 flex items-center gap-2">
          <Rocket className="h-6 w-6" style={{ color: "var(--sage)" }} />
          Kickstart nieuwe KDV
        </h1>
        <p className="text-sm text-neutral-500 mt-1">Zet een nieuwe organisatie in 3 stappen live — intake → opties → welkomstmail.</p>
      </div>

      {/* Steps indicator */}
      <div className="flex items-center gap-2 mb-6" data-testid="wizard-steps">
        {[1, 2, 3].map(n => (
          <div key={n} className="flex items-center gap-2 flex-1">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                step >= n
                  ? "bg-[var(--sage)] text-white"
                  : "bg-neutral-100 text-neutral-500 border border-neutral-200"
              }`}
              style={step >= n ? { background: "var(--sage)" } : {}}
              data-testid={`wizard-step-${n}`}
            >{step > n ? <CheckCircle2 className="h-4 w-4" /> : n}</div>
            <div className={`text-xs font-medium ${step >= n ? "text-neutral-900" : "text-neutral-400"}`}>
              {n === 1 ? "Intake" : n === 2 ? "Opties" : "Bevestiging"}
            </div>
            {n < 3 && <div className={`h-px flex-1 ${step > n ? "bg-[var(--sage)]" : "bg-neutral-200"}`} style={step > n ? { background: "var(--sage)" } : {}} />}
          </div>
        ))}
      </div>

      {step === 1 && (
        <Card className="p-6 bg-white border-neutral-200" data-testid="wizard-step-intake">
          <div className="text-sm font-semibold text-neutral-900 mb-4 flex items-center gap-2"><Building2 className="h-4 w-4" style={{ color: "var(--sage)" }} /> Basisgegevens</div>
          <div className="grid gap-4 max-w-xl">
            <div>
              <Label htmlFor="org-name">Naam van de KDV *</Label>
              <Input id="org-name" data-testid="wizard-input-name" value={form.name} onChange={setF("name")} placeholder="bijv. Kinderopvang Zonneschijn" />
            </div>
            <div>
              <Label htmlFor="org-slug">Slug (URL-vriendelijk) — optioneel</Label>
              <Input id="org-slug" data-testid="wizard-input-slug" value={form.slug} onChange={setF("slug")} placeholder={slugPreview || "auto-gegenereerd"} />
              <div className="text-[11px] text-neutral-500 mt-1">Wordt: <span className="font-mono text-neutral-700">{slugPreview || "—"}</span></div>
            </div>
            <div>
              <Label htmlFor="org-kvk">KVK-nummer — optioneel</Label>
              <Input id="org-kvk" data-testid="wizard-input-kvk" value={form.kvk} onChange={setF("kvk")} placeholder="12345678" maxLength={12} />
            </div>
            <div>
              <Label htmlFor="org-email">E-mail directeur *</Label>
              <Input id="org-email" data-testid="wizard-input-email" type="email" value={form.email} onChange={setF("email")} placeholder="directeur@voorbeeld.nl" />
              <div className="text-[11px] text-neutral-500 mt-1"><Mail className="h-3 w-3 inline mr-1" /> Ontvangt een magic-link om te starten.</div>
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button
              onClick={() => { if (validateStep1()) setStep(2); }}
              className="text-white"
              style={{ background: "var(--sage)" }}
              data-testid="wizard-next-1"
            >Volgende <ArrowRight className="h-4 w-4 ml-2" /></Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 bg-white border-neutral-200" data-testid="wizard-step-options">
          <div className="text-sm font-semibold text-neutral-900 mb-4 flex items-center gap-2"><Sparkles className="h-4 w-4" style={{ color: "var(--sage)" }} /> Opties</div>
          <div className="space-y-4 max-w-xl">
            <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-sm text-emerald-900">
              <strong>Standaard actief</strong> voor elke nieuwe KDV:
              <ul className="list-disc ml-5 mt-1 text-emerald-800">
                <li>Contractpakketten (KDV standaard)</li>
                <li>Beleidsdocumenten template</li>
                <li>Wet Kinderopvang policy-settings</li>
                <li>Onboarding-checklist (KVK, tekenbevoegde, locaties, groepen, betalingen)</li>
              </ul>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-md border border-neutral-200 bg-white">
              <Switch id="seed-demo" checked={form.seed_demo} onCheckedChange={setF("seed_demo")} data-testid="wizard-switch-demo" />
              <div>
                <Label htmlFor="seed-demo" className="text-sm font-medium cursor-pointer">Vul extra starter-templates in</Label>
                <div className="text-xs text-neutral-500 mt-1">Extra dagritme-voorbeelden en activiteiten-bibliotheek. Handig voor demo's; verwijder gerust in productie.</div>
              </div>
            </div>
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)} data-testid="wizard-back-1"><ArrowLeft className="h-4 w-4 mr-2" /> Terug</Button>
            <Button
              onClick={submit}
              disabled={busy}
              className="text-white"
              style={{ background: "var(--sage)" }}
              data-testid="wizard-submit"
            >{busy ? "Bezig..." : <>Aanmaken & welkomstmail <Rocket className="h-4 w-4 ml-2" /></>}</Button>
          </div>
        </Card>
      )}

      {step === 3 && result && (
        <Card className="p-6 bg-white border-neutral-200" data-testid="wizard-step-done">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--sage)" }}>
              <CheckCircle2 className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="text-lg font-semibold text-neutral-900">{form.name} is gestart</div>
              <div className="text-sm text-neutral-500 mt-1">Alle standaard-templates zijn geplaatst.</div>
            </div>
          </div>

          <div className="space-y-2 text-sm mb-6">
            <ChecklistItem done label="Organisatie aangemaakt" detail={<span className="font-mono text-xs">{result.org_id}</span>} />
            <ChecklistItem done label="Onboarding-sessie geïnitialiseerd" detail="Status: uitgenodigd" />
            <ChecklistItem
              done={(() => {
                const s = result.seed_backend || result.starter_seed;
                if (!s) return false;
                // Backend retourneert counters (contract_packages, policy_documents,
                // policy_settings) OF een {ok:true} object. Beide gelden als
                // 'geplaatst' zolang er tenminste 1 item verwerkt is.
                if (s.ok) return true;
                return Object.values(s).some(v => (typeof v === "number" && v > 0) || v === true);
              })()}
              label="Starter-templates geplaatst"
              detail={(() => {
                const s = result.seed_backend || result.starter_seed;
                if (!s) return "Overgeslagen";
                const parts = [];
                if (s.contract_packages) parts.push(`${s.contract_packages} contractpakketten`);
                if (s.policy_documents) parts.push(`${s.policy_documents} beleidsdocumenten`);
                if (s.policy_settings) parts.push("policy-settings");
                return parts.length ? parts.join(" · ") : (s.error || "Contractpakketten, beleid, policies");
              })()}
            />
            <ChecklistItem
              done={!!(result.email_result?.sent)}
              label="Welkomstmail met magic-link verzonden"
              detail={result.email_result?.sent ? form.email : (result.email_result?.error ? `Niet verzonden: ${result.email_result.error}` : "Nog niet verzonden")}
            />
            <ChecklistItem done={!!result.demo_seed?.ok} label="Extra starter-templates" detail={form.seed_demo ? (result.demo_seed?.ok ? "geplaatst" : "overgeslagen (fout)") : "niet gevraagd"} />
            <ChecklistItem done={false} label="Eerste-week checklist voor directeur" detail="Wacht op magic-link login" />
          </div>

          <div className="p-3 rounded-md bg-sky-50 border border-sky-200 text-sm text-sky-900 mb-6">
            <strong>Wat gebeurt er nu?</strong>
            <ol className="list-decimal ml-5 mt-1 space-y-0.5 text-sky-800">
              <li>De directeur ontvangt een welkomstmail met magic-link.</li>
              <li>Hij/zij logt in en doorloopt de onboarding-checklist (KVK/tekenbevoegde/locaties/groepen).</li>
              <li>Bij "Indienen ter goedkeuring" krijg jij als platform-super een review-taak op <code>/platform/onboardingen</code>.</li>
              <li>Na jouw goedkeuring gaat de KDV live en verschijnt hij bovenaan de organisatielijst.</li>
            </ol>
          </div>

          {result.email_result?.magic_link && (
            <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-sm mb-6" data-testid="wizard-magic-link">
              <div className="font-semibold text-amber-900 mb-1">
                {result.email_result?.sent ? "Magic-link (voor jouw referentie)" : "Welkomstmail kon niet verstuurd worden — kopieer de magic-link hieronder"}
              </div>
              <div className="font-mono text-xs text-amber-900 break-all bg-white/60 p-2 rounded border border-amber-200 select-all">
                {result.email_result.magic_link}
              </div>
              <Button
                size="sm"
                variant="outline"
                className="mt-2 border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
                onClick={() => { navigator.clipboard.writeText(result.email_result.magic_link); toast.success("Magic-link gekopieerd"); }}
                data-testid="wizard-copy-magic-link"
              >Kopieer link</Button>
            </div>
          )}

          <div className="flex gap-2 flex-wrap">
            <Button onClick={() => navigate(`/platform/organisaties/${result.org_id}`)} className="text-white" style={{ background: "var(--sage)" }} data-testid="wizard-open-detail">
              Open organisatiedetail
            </Button>
            <Button variant="outline" onClick={() => navigate("/platform/organisaties")} data-testid="wizard-back-overview">
              Terug naar overzicht
            </Button>
            <Button variant="outline" onClick={() => { setStep(1); setForm({ name: "", slug: "", kvk: "", email: "", seed_demo: false }); setResult(null); }} data-testid="wizard-another">
              Nog een KDV toevoegen
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function ChecklistItem({ done, label, detail }) {
  return (
    <div className="flex items-start gap-2">
      <div className={`w-5 h-5 rounded-full mt-0.5 flex items-center justify-center shrink-0 ${done ? "bg-emerald-100 text-emerald-700" : "bg-neutral-100 text-neutral-400"}`}>
        {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : <span className="text-[10px]">…</span>}
      </div>
      <div>
        <div className="text-sm text-neutral-900 font-medium">{label}</div>
        {detail && <div className="text-xs text-neutral-500">{detail}</div>}
      </div>
    </div>
  );
}
